import { useCallback, useEffect, useRef, useState } from "react";

export default function Testimonials() {
  const scrollRef = useRef(null);
  const [index, setIndex] = useState(0);

  const getCardStep = useCallback(() => {
    const container = scrollRef.current;

    if (!container || !container.firstChild) return 0;

    const card = container.firstChild;
    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || 0);

    return card.offsetWidth + gap;
  }, []);

  const scrollToIndex = useCallback(
    (i) => {
      const container = scrollRef.current;

      if (!container) return;

      container.scrollTo({
        left: i * getCardStep(),
        behavior: "smooth",
      });

      setIndex(i);
    },
    [getCardStep]
  );

  const next = useCallback(() => {
    const container = scrollRef.current;

    if (!container) return;

    const total = container.children.length;
    const newIndex = (index + 1) % total;

    scrollToIndex(newIndex);
  }, [index, scrollToIndex]);

  const prev = useCallback(() => {
    const container = scrollRef.current;

    if (!container) return;

    const total = container.children.length;
    const newIndex = (index - 1 + total) % total;

    scrollToIndex(newIndex);
  }, [index, scrollToIndex]);

  // Auto-slide every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const container = scrollRef.current;

      if (!container) return;

      const total = container.children.length;

      setIndex((currentIndex) => {
        const newIndex = (currentIndex + 1) % total;

        container.scrollTo({
          left: newIndex * getCardStep(),
          behavior: "smooth",
        });

        return newIndex;
      });
    }, 6000);

    return () => clearInterval(interval);
  }, [getCardStep]);

  return (
    <section className="px-4 sm:px-5 py-10 md:py-12">
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="flex items-center justify-center gap-3">
          <div className="h-px w-8 md:w-12 bg-[#D07728]/60" />

          <h3 className="text-xs md:text-sm uppercase tracking-wide whitespace-nowrap">
            WHAT READERS SAY
          </h3>

          <div className="h-px w-8 md:w-12 bg-[#D07728]/60" />
        </div>

        <h2 className="text-2xl md:text-4xl mb-6 mt-2 text-center leading-tight">
          Voices on
          <span className="text-[#D07728]"> Decolonizing Mission</span>
        </h2>

        {/* Carousel */}
        <div className="relative">
          {/* Desktop left arrow */}
          <button
            onClick={prev}
            aria-label="Previous testimonial"
            className="
              hidden md:flex
              absolute -left-5 lg:-left-12
              top-1/2 -translate-y-1/2
              w-10 h-10
              items-center justify-center
              bg-white shadow-md
              rounded-full
              z-10
              text-lg
            "
          >
            ←
          </button>

          {/* Scroll container */}
          <div
            ref={scrollRef}
            className="
              flex
              gap-4 md:gap-6
              overflow-hidden
              scroll-smooth
              snap-x snap-mandatory
            "
          >
            <div
              className="
                w-[88%] sm:w-[75%] md:w-[320px]
                flex-shrink-0
                snap-start
                p-5 md:p-6
                rounded-xl
                bg-[#Fcfbfa]
              "
            >
              <p className="text-gray-600 leading-relaxed text-left">
                “Here is the passionate voice of a rising scholar from the
                global south, exposing the entanglement of the gospel with the
                systematic legacy of European expansionism, white supremacy and
                the civilising mission of colonialism.”
              </p>

              <div className="mt-5 text-sm">
                <p className="font-medium">
                  Prof JNJ (Klippies) Kritzinger
                </p>

                <p className="italic text-gray-500 mt-1">
                  Emeritus Professor of Missiology
                  <br />
                  University of South Africa
                </p>
              </div>
            </div>

            <div
              className="
                w-[88%] sm:w-[75%] md:w-[320px]
                flex-shrink-0
                snap-start
                p-5 md:p-6
                rounded-xl
                bg-[#Fcfbfa]
              "
            >
              <p className="text-gray-600 leading-relaxed text-left">
                “Kwiyani invites us to read the past with critical realist
                spectacles, rather than merely narrating and re-narrating the
                triumph of the missionaries, without discarding the sacrifice
                of missionaries in their efforts to reach people with the
                gospel in Africa.”
              </p>

              <div className="mt-5 text-sm">
                <p className="font-medium">Dr Abeneazer G. Urga</p>

                <p className="italic text-gray-500 mt-1">
                  Lecturer
                  <br />
                  Evangelical Theological College, Addis Ababa, Ethiopia
                </p>
              </div>
            </div>

            <div
              className="
                w-[88%] sm:w-[75%] md:w-[320px]
                flex-shrink-0
                snap-start
                p-5 md:p-6
                rounded-xl
                bg-[#Fcfbfa]
              "
            >
              <p className="text-gray-600 leading-relaxed text-left">
                “From his home country of Malawi to Jesuits in Asia to first
                century Israel, Kwiyani examines the intersection of Christian
                mission and imperial power and proposes a fresh approach to
                God's mission to the world.”
              </p>

              <div className="mt-5 text-sm">
                <p className="font-medium">Prof Emma Wild-Wood</p>

                <p className="italic text-gray-500 mt-1">
                  Centre for the Study of World Christianity
                  <br />
                  University of Edinburgh
                </p>
              </div>
            </div>

            <div
              className="
                w-[88%] sm:w-[75%] md:w-[320px]
                flex-shrink-0
                snap-start
                p-5 md:p-6
                rounded-xl
                bg-[#Fcfbfa]
              "
            >
              <p className="text-gray-600 leading-relaxed text-left">
                “A bold and necessary call to reimagine Christian mission in a
                postcolonial world... This is not merely a critique but a
                hopeful blueprint for a decolonised missiology that resonates
                with the diverse, global Body of Christ.”
              </p>

              <div className="mt-5 text-sm">
                <p className="font-medium">Prof Esther Mombo</p>

                <p className="italic text-gray-500 mt-1">
                  Professor, School of Theology
                  <br />
                  St Paul's University, Kenya
                </p>
              </div>
            </div>

            <div
              className="
                w-[88%] sm:w-[75%] md:w-[320px]
                flex-shrink-0
                snap-start
                p-5 md:p-6
                rounded-xl
                bg-[#Fcfbfa]
              "
            >
              <p className="text-gray-600 leading-relaxed text-left">
                “God's mission decolonises. This is the central claim of this
                challenging book which not only tells the history of mission
                from the perspective of the colonised but also asks how do we
                engage in mission in a world without depending on empires.”
              </p>

              <div className="mt-5 text-sm">
                <p className="font-medium">Dr Cathy Ross</p>

                <p className="italic text-gray-500 mt-1">
                  Pioneer Mission Training Lead
                  <br />
                  Church Mission Society, Oxford
                </p>
              </div>
            </div>
          </div>

          {/* Desktop right arrow */}
          <button
            onClick={next}
            aria-label="Next testimonial"
            className="
              hidden md:flex
              absolute -right-5 lg:-right-12
              top-1/2 -translate-y-1/2
              w-10 h-10
              items-center justify-center
              bg-white shadow-md
              rounded-full
              z-10
              text-lg
            "
          >
            →
          </button>

          {/* Mobile controls */}
          <div className="flex md:hidden justify-center items-center gap-4 mt-5">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              className="
                w-9 h-9
                flex items-center justify-center
                border border-gray-300
                rounded-full
                text-base
              "
            >
              ←
            </button>

            <span className="text-xs text-gray-500">
              {index + 1} / 5
            </span>

            <button
              onClick={next}
              aria-label="Next testimonial"
              className="
                w-9 h-9
                flex items-center justify-center
                border border-gray-300
                rounded-full
                text-base
              "
            >
              →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}